'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** cn_opening : a window or a door
//***********************************************************************************
//***********************************************************************************

import { cn_element } from './cn_element';
import { cn_add, cn_box, cn_clone, cn_dot, cn_middle, cn_mul, cn_normal, cn_normalize, cn_sub, cnx_add, cnx_mul } from '../utils/cn_utilities';
import { fh_cross, fh_matrix, fh_mul, fh_polygon } from '@acenv/fh-3d-viewer';
import { cn_camera } from '../svg/cn_camera';
import { cn_element_visitor } from '../utils/visitors/cn_element_visitor';
import { cn_opening_type } from './cn_opening_type';
import { cn_vertex } from './cn_vertex';
import { cn_wall } from './cn_wall';
import { cn_transaction_manager } from '../utils/cn_transaction_manager';
import { cn_constant, CN_FACING_SUPPORTS, CN_OPENING_FACING_CLOSING, CN_OPENING_FACING_MAIN, CN_OPENING_FACING_OPENING, CN_OPENING_FACING_SASH, CN_OPENING_FACING_SILL, CN_OPENING_FACING_TRANSOM } from './cn_model_constants';
import { cn_plugin_option } from '../utils/cn_plugin_option';
import { cn_facing_group } from './cn_facing';

export class cn_opening extends cn_element {
    constructor(opening_type) {
        super();
        this.class_name = 'cn_opening';
        this.wall = null;

        //*** Dimensions. If < 0, use the opening type. */
        this.width = -1;
        this.height = -1;
        this.z = -1;
        this.position = 0;
        this.opening_type = opening_type;
        this.valid = true;
        this.draw_priority = 10;
        this.opening_start = true;
        this.opening_inside = true;
        this.opening_position = 1;
        /** @type {cn_vertex} */
        this.wall_lock_vertex = null;
        this.wall_position = 0;
        this.wall_interface = null;
        // Dormant, Ouvrant, Allège, Imposte, protection pour côté 1 et côté 2.
        this.facings = [{}, {}];
        this.substrates = [{}, {}];
        this.thickness = 0.06;
    }

    //***********************************************************************************
    //**** Clone
    //***********************************************************************************
    clone() {
        var c = new cn_opening(this.opening_type);
        c.parent = this.parent;
        c.wall = this.wall;
        c.width = this.width;
        c.height = this.height;
        c.z = this.z;
        c.position = this.position;
        c.valid = this.valid;
        c.draw_priority = this.draw_priority;
        c.opening_start = this.opening_start;
        c.opening_inside = this.opening_inside;
        c.opening_position = this.opening_position;
        c.facings = cn_facing_group.clone(this.facings);
        c.substrates = cn_facing_group.clone(this.substrates);
        c.wall_lock_vertex = this.wall_lock_vertex;

        return c;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = this.serialize_element();
        json.width = this.width;
        json.height = this.height;
        json.z = this.z;
        json.opening_type = this.opening_type.ID;
        json.opening_start = this.opening_start;
        json.opening_inside = this.opening_inside;
        json.opening_position = this.opening_position;
        json.facings = cn_facing_group.serialize(this.facings, this.get_facing_groups());
        json.substrates = cn_facing_group.serialize(this.substrates, this.get_facing_groups());
        // @ts-ignore
        json.wall_lock_vertex = (this.wall_lock_vertex) ? this.wall_lock_vertex.s_index : -1;
        json.position = this.position;
        return json;
    }

    static unserialize(json, scene) {
        if (typeof (json) != 'object') return null;
        if (typeof (json.position) != 'number' && typeof (json.wall_position) != 'number') return null;

        var ot = null;

        if (typeof (json.opening_type) == 'string')
            ot = scene.building.get_element_type(json.opening_type);

        if (ot == null) {
            var category = 'window';
            if (typeof (json.opening_type) == 'object' && typeof (json.opening_type.category) == 'string')
                category = json.opening_type.category;
            if (category == 'window')
                ot = scene.building.get_window_types()[0];
            else
                ot = scene.building.get_door_types()[0];
        }

        var opening = new cn_opening(ot);
        opening.unserialize_element(json);

        if (typeof (json.width) == 'number')
            opening.width = json.width;

        if (typeof (json.height) == 'number')
            opening.height = json.height;

        if (typeof (json.z) == 'number')
            opening.z = json.z;

        if (typeof (json.opening_start) == 'boolean')
            opening.opening_start = json.opening_start;

        if (typeof (json.opening_inside) == 'boolean')
            opening.opening_inside = json.opening_inside;

        if (typeof (json.opening_position) == 'number')
            opening.opening_position = json.opening_position;

        cn_facing_group.unserialize_facings(opening.facings, json, scene.building);

        cn_facing_group.unserialize_substrates(opening.substrates, json.substrates, scene.building.get_mmm_compositions());

        if (typeof (json.position) == 'number')
            opening.position = json.position;

        if (typeof (json.wall_lock_vertex) == 'number') {
            opening.wall_lock_vertex = scene.vertices[json.wall_lock_vertex] || null;
            opening.wall_position = NaN;
        }

        if (typeof (json.wall_position) == 'number') {
            opening.position = json.wall_position;
            if (typeof (json.wall_lock) == 'number' && json.wall_lock)
                opening["obsolete_lock"] = json.wall_position;
        }

        return opening;
    }

    /**
     * returns the opening width
     * @returns {number}
     */
    get_width() {
        if (this.width > 0) return this.width;
        return this.opening_type.width;
    }

    /**
     * returns the opening height
     * @returns {number}
     */
    get_height() {
        if (this.height > 0) return this.height;
        return this.opening_type.height;
    }

    /**
     * returns the opening width
     * @returns {number}
     */
    get_z() {
        if (this.opening_type.category == "door") return 0;
        if (this.z >= 0) return this.z;
        return this.opening_type.z;
    }

    /**
     * returns derivate opening type
     * @returns {cn_opening_type}
     */
    get_opening_type() {
        if (this.width > 0 || this.height > 0 || this.z >= 0)
            return this.opening_type.get_derivate(this.get_width(), this.get_height(), this.get_z());
        return this.opening_type;
    }

    update_wall_position() {
        if (this["obsolete_lock"]) {
            this.position = this.wall.bounds.length - this["obsolete_lock"];
            this.wall_lock_vertex = this.wall.vertices[1];
            this["obsolete_lock"] = undefined;
        }
        let candidate_locks = this.get_candidate_locks();
        if (!candidate_locks.includes(this.wall_lock_vertex))
            this.wall_lock_vertex = candidate_locks[0];
        this.wall_position = this.position - this.wall.compute_abcissa(this.wall_lock_vertex.position);
    }

    update_position() {
        if (!isNaN(this.wall_position) && this.get_candidate_locks().includes(this.wall_lock_vertex))
            this.position = this.wall.compute_abcissa(this.wall_lock_vertex.position) + this.wall_position;
        else
            this.update_wall_position();
    }

    /**
     * Returns potential vertex lock for each of 6 possible locks
     * @returns {cn_vertex[]}
     */
    get_candidate_locks() {
        return [this.wall.vertices[0], this.wall.vertices[1]].concat(this.wall.supported_vertices.filter(v => v.support_wall_side < 2 && v.walls.length));
    }

    /**
     * Retruns a readable ID
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {string}
     */
    build_readable_id(storey) {
        if (!this.valid || !this.wall_interface)
            return super.build_readable_id(storey);
        let side = this.wall_interface.spaces.findIndex(sp => !sp.outside);
        if (side < 0) side = 0;
        return this.wall_interface.space_segments[side].build_readable_id(storey) + "B" + (1 + this.wall.openings.indexOf(this));
    }

    //***********************************************************************************
    //**** Draw the opening in svg
    //***********************************************************************************
    draw(camera, add_classes, fill = '') {
        var html = '';
        if (this.wall == null) return html;

        const v0 = cn_mul(this.wall.bounds.direction, this.position);
        const v1 = cn_mul(this.wall.bounds.direction, this.position + this.get_width());
        const p0 = camera.world_to_screen(cn_add(this.wall.bounds.pmin, v0));
        const p1 = camera.world_to_screen(cn_add(this.wall.bounds.pmin, v1));
        const p2 = camera.world_to_screen(cn_add(this.wall.bounds.pmax, v1));
        const p3 = camera.world_to_screen(cn_add(this.wall.bounds.pmax, v0));

        if (add_classes.indexOf('mouseover') >= 0 || add_classes.indexOf('selected') >= 0) {
            const highlight = (add_classes.indexOf('selected') >= 0) ? 'selected' : 'mouseover';
            html += '<path class=\'opening_highlight ' + highlight + '\' d=\'';
            html += ' M ' + p0[0] + ' ' + p0[1];
            html += ' L ' + p1[0] + ' ' + p1[1];
            html += ' ' + p2[0] + ' ' + p2[1];
            html += ' ' + p3[0] + ' ' + p3[1] + 'Z\' />';
        }

        if (!this.valid) {
            html += '<path class="opening not_valid" d="';
            html += ' M ' + p0[0] + ' ' + p0[1];
            html += ' L ' + p1[0] + ' ' + p1[1];
            html += ' ' + p2[0] + ' ' + p2[1];
            html += ' ' + p3[0] + ' ' + p3[1] + 'Z" />';
            return html;
        }

        let draw_class = 'opening opening_category_' + this.opening_type.category;
        if (add_classes)
            draw_class += ' ' + add_classes.join(' ');
        html += '<path class=\'' + draw_class + '\' ' + fill + ' d=\'';
        html += ' M ' + p0[0] + ' ' + p0[1];
        html += ' L ' + p1[0] + ' ' + p1[1];
        html += ' ' + p2[0] + ' ' + p2[1];
        html += ' ' + p3[0] + ' ' + p3[1] + 'Z\' />';

        if (this.opening_type.free) {
            return html;
        }

        //*** Compute opening axis
        var c0, c1;
        var thickness = this.thickness * camera.world_to_screen_scale;
        var wall_thickness = this.wall.wall_type.thickness * camera.world_to_screen_scale;
        var x = 0.5;
        if (this.opening_position != 1) {
            var x = 0.5 * thickness / wall_thickness;
            if (this.opening_position == 2) x = 1 - x;
        }
        c0 = cn_add(cn_mul(p0, 1 - x), cn_mul(p3, x));
        c1 = cn_add(cn_mul(p1, 1 - x), cn_mul(p2, x));

        if (!this.opening_start) {
            var c = c1;
            c1 = c0;
            c0 = c;
        }

        //*** Draw opening position
        var direction = cn_sub(c1, c0);
        var normal = cn_normal(direction);
        cn_normalize(normal);
        normal = fh_mul(normal, 0.5 * thickness);

        html += `<path class="${draw_class}" ${fill} d="
         M ${c0[0] - normal[0]} ${c0[1] - normal[1]} L ${c1[0] - normal[0]} ${c1[1] - normal[1]} ${c1[0] + normal[0]}  ${c1[1] + normal[1]} ${c0[0] + normal[0]} ${c0[1] + normal[1]} Z" />`;

        var nb_panels = parseInt(this.opening_type.panels);
        var vertices = [];
        vertices.push(c0);
        if (nb_panels > 0) {
            var panel_ratios = this.opening_type.compute_panel_widths();
            var panel_accum = 0;
            panel_ratios.forEach(p => panel_accum += p);

            var dd = cn_sub(c1, c0);
            var x = 0;
            for (var n = 0; n < nb_panels - 1; n++) {
                x += panel_ratios[n] / panel_accum;
                vertices.push(cn_add(c0, cn_mul(dd, x)));
            }
        }
        vertices.push(c1);

        var extra = (add_classes.indexOf('exp') >= 0) ? 'exp' : '';

        if (this.opening_type.opening == 'french') {
            var angle = Math.PI * 0.2;
            if (this.opening_type.category == 'door')
                angle = Math.PI * 0.5;
            if (!this.opening_inside) angle *= -1;

            for (var k = 0; k < nb_panels; k++) {
                if (k == 0)
                    html += this._draw_panel_swing(vertices[k], vertices[k + 1], thickness, angle, extra, fill);
                else
                    html += this._draw_panel_swing(vertices[k + 1], vertices[k], thickness, -angle, extra, fill);
            }
        } else if (this.opening_type.opening == 'none') {
            for (var k = 0; k < nb_panels; k++)
                html += this._draw_panel(vertices[k], vertices[k + 1], thickness, extra, fill);
        } else if (this.opening_type.opening == 'sliding' || (this.opening_type.opening == 'vertical' && this.opening_type.vertical_opening == 'sash')) {
            if (nb_panels == 1)
                html += this._draw_panel(c0, c1, thickness, extra, fill);
            else {
                var dt = cn_normal(cn_sub(c1, c0));
                cn_normalize(dt);
                if (this.opening_type.opening == 'sliding') {
                    dt = cn_mul(dt, thickness * 0.5);
                    for (var k = 0; k < nb_panels; k++) {
                        html += this._draw_panel(cn_add(vertices[k], dt), cn_add(vertices[k + 1], dt), thickness, extra, fill);
                        dt = cn_mul(dt, -1);
                    }
                } else {
                    var dt0 = cn_mul(dt, 0.5 * nb_panels * thickness - 0.5 * thickness);
                    dt = cn_mul(dt, thickness);
                    for (var k = 0; k < nb_panels; k++) {
                        html += this._draw_panel(cn_add(c0, dt0), cn_add(c1, dt0), thickness, extra, fill);
                        dt0 = cn_sub(dt0, dt);
                    }
                }
            }
        } else if (this.opening_type.opening == 'vertical') {
            var dt = cn_normal(cn_sub(c1, c0));
            cn_normalize(dt);
            var length = (this.opening_type.vertical_opening == 'tilting') ? 0.8 : 0.4;
            length *= camera.world_to_screen_scale;
            if (this.opening_type.vertical_opening != 'tilting') {
                const mult = (!this.opening_inside) ? -1 : 1;
                const dt0 = cn_mul(dt, length * 0.5 * mult);
                html += this._draw_panel(cn_add(c0, dt0), cn_add(c1, dt0), length, extra + ' ghost');
            } else
                html += this._draw_panel(c0, c1, length, extra + ' ghost');
        } else if (this.opening_type.opening === 'folding') {
            const p00 = [...vertices[0]]
            const p01 = cn_middle(cn_middle(p00, vertices[vertices.length - 1]), p00)
            const dx = cn_sub(p01, p00);
            const l = cn_normalize(dx);
            const dy = cn_mul(cn_normal(dx), this.opening_inside ? 1 : -1);
            const da = cn_add(cn_mul(dx, Math.cos(Math.PI / 3)), cn_mul(dy, Math.sin(Math.PI / 3)));
            const dt = cn_normal(da);
            const p = cn_add(p00, cn_mul(dt, thickness * 0.5));
            const pA = cn_add(p, cn_mul(da, l));
            const pB = cn_sub(pA, cn_mul(dt, thickness));
            const pC = cn_sub(pB, cn_mul(da, l));
            html += `<path class="opening opening_category_${this.opening_type.category} ${extra}" ${fill} d="
             M ${p[0]} ${p[1]} L ${pA[0]} ${pA[1]} ${pB[0]} ${pB[1]} ${pC[0]} ${pC[1]} Z" />`;
            const polar = 2 * Math.atan2(p01[1] - p00[1], p01[0] - p00[0])
            const contact = this.opening_inside ? pB : pA;
            html += `<path transform="matrix(${-Math.cos(polar)}, ${-Math.sin(polar)}, ${-Math.sin(polar)}, ${Math.cos(polar)}, 0, 0)" transform-origin="${contact[0]} ${contact[1]}" class="opening opening_category_${this.opening_type.category} ${extra}" ${fill} d="
             M ${p[0]} ${p[1]} L ${pA[0]} ${pA[1]} ${pB[0]} ${pB[1]} ${pC[0]} ${pC[1]} Z" />`;
        }

        return html;
    }

    draw_id_label(camera, storey) {
        const v0 = cn_mul(this.wall.bounds.direction, this.position);
        const v1 = cn_mul(this.wall.bounds.direction, this.position + this.get_width());
        const p0 = camera.world_to_screen(cn_add(this.wall.bounds.pmin, v0));
        const p2 = camera.world_to_screen(cn_add(this.wall.bounds.pmax, v1));
        return this._draw_id_label(camera, storey, cn_middle(p0, p2));
    }

    _draw_panel_swing(p0, p1, thickness, angle, extra = '', fill = '') {
        var dx = cn_sub(p1, p0);
        var l = cn_normalize(dx);
        var dy = cn_normal(dx);
        var dd = cn_add(cn_mul(dx, Math.cos(angle)), cn_mul(dy, Math.sin(angle)));
        var dt = cn_normal(dd);
        var p;

        var html = '';
        html += '<path class=\'opening opening_category_' + this.opening_type.category + ' ' + extra + '\' ' + fill + ' d=\'';
        p = cn_add(p0, cn_mul(dt, thickness * 0.5));
        html += ' M ' + p[0] + ' ' + p[1];
        p = cn_add(p, cn_mul(dd, l));
        html += ' L ' + p[0] + ' ' + p[1];
        p = cn_sub(p, cn_mul(dt, thickness));
        html += ' ' + p[0] + ' ' + p[1];
        p = cn_sub(p, cn_mul(dd, l));
        html += ' ' + p[0] + ' ' + p[1];
        html += ' Z\' />';

        html += '<path class=\'opening_door opening_category_' + this.opening_type.category + ' ' + extra + '\' ' + ' d=\'';
        html += ' M ' + p1[0] + ' ' + p1[1];
        var lambda = l * (1 - cn_dot(dx, dd)) / cn_dot(dy, dd);
        p = cn_add(p1, cn_mul(dy, lambda));
        html += ' Q ' + p[0] + ' ' + p[1];
        p = cn_add(p0, cn_mul(dd, l));
        html += ' ' + p[0] + ' ' + p[1];
        html += '\' />';
        return html;
    }

    _draw_panel(p0, p1, thickness, extra = '', fill = '') {
        var dt = cn_normal(cn_sub(p1, p0));
        cn_normalize(dt);
        dt = cn_mul(dt, thickness * 0.5);
        var p;
        var html = '';
        html += '<path class=\'opening opening_category_' + this.opening_type.category + ' ' + extra + '\' ' + fill + ' d=\'';
        p = cn_add(p0, dt);
        html += ' M ' + p[0] + ' ' + p[1];
        p = cn_add(p1, dt);
        html += ' L ' + p[0] + ' ' + p[1];
        p = cn_sub(p1, dt);
        html += ' ' + p[0] + ' ' + p[1];
        p = cn_sub(p0, dt);
        html += ' ' + p[0] + ' ' + p[1];
        html += ' Z\' />';

        return html;
    }


    //***********************************************************************************
    /**
     * Draws in svg the measures of the opening (width, height, z)
     * @param {cn_camera} camera
     * @param {string[]} add_classes
     * @param {boolean | {selected:boolean, position:number[]}} opening_measure
     * @param {boolean | Array<{selected:boolean, position:number[]}>} opening_wall_measure
     * @returns {string} html string
     */
    draw_measures(camera, add_classes, opening_measure = true, opening_wall_measure = true) {
        var html = '';
        if (!this.wall || !this.valid) return html;
        if (!opening_wall_measure && !opening_measure) return html;

        let width = this.get_width();
        var p0_0 = cn_add(this.wall.bounds.pmin, cn_mul(this.wall.bounds.direction, this.position));
        var p0_1 = cn_add(this.wall.bounds.pmin, cn_mul(this.wall.bounds.direction, this.position + width));
        var nn = cn_mul(this.wall.bounds.normal, this.wall.wall_type.thickness);
        var p1_0 = cn_add(p0_0, nn);
        var p1_1 = cn_add(p0_1, nn);

        //*** Draw the measure of the opening itself */
        if (opening_measure) {
            const save_results = typeof (opening_measure) == 'object';
            var p0, p1;
            if (this.wall_interface && this.wall_interface.spaces[1].outside) {
                p0 = p1_1;
                p1 = p1_0;
            } else {
                p0 = p0_0;
                p1 = p0_1;
            }

            var txt = '';
            txt += (width * 100).toFixed(0) + ' x ' + (this.get_height() * 100).toFixed(0);
            if (this.get_z() > 0) txt += ' z=' + (this.get_z() * 100).toFixed(0);

            html = camera.draw_measure(p0, p1, (typeof (opening_measure) == 'object') ? opening_measure.position : null, (typeof (opening_measure) == 'object') ? opening_measure.selected : false, 40, add_classes, txt);
        }

        //*** Draw the measure of the opening to what is surrounding */
        if (opening_wall_measure) {
            const save_results = typeof (opening_wall_measure) == 'object';
            for (var sense = 0; sense < 2; sense++) {

                let t_opening = 0;
                let t = this.position + width * sense;

                //*** Maybe an opening? */
                const other_opening = (sense > 0) ? this.next_valid_opening() : this.previous_valid_opening();
                if (other_opening)
                    t_opening = (sense > 0) ? other_opening.position : other_opening.position + other_opening.get_width();
                else
                    t_opening = NaN;

                for (var side = 0; side < 2; side++) {
                    let t0 = t_opening;
                    const space_segment = this.wall.space_segments.filter(ss => ss.wall_side == side).find(ss => t >= ss.wall_positions[side] && t <= ss.wall_positions[1 - side]);
                    if (space_segment) {
                        let t_ss = space_segment.wall_positions[(sense == side) ? 0 : 1];
                        if (isNaN(t_opening) || (sense > 0 && t_ss < t_opening) || (sense == 0 && t_ss > t_opening))
                            t0 = t_ss;
                    }

                    if (!isNaN(t0)) {
                        const p0 = this.wall.compute_side_position_from_abcissa((sense == side) ? t0 : t, side);
                        const p1 = this.wall.compute_side_position_from_abcissa((sense == side) ? t : t0, side);
                        html += camera.draw_measure(p0, p1, (save_results) ? opening_wall_measure[sense * 2 + side].position : null, (save_results) ? opening_wall_measure[sense * 2 + side].selected : false, 40, add_classes);
                    }
                    else if (save_results)
                        opening_wall_measure[sense * 2 + side].position = [NaN, NaN, NaN];
                }
            }
            /*var op_prec = null;
            var op_next = null;
            var op_found = false;
            for (var i in this.wall.openings) {
                var op = this.wall.openings[i];
                if (!op.valid) continue;
                if (op == this) {
                    op_found = true;
                    continue;
                }
                if (op_found) {
                    op_next = op;
                    break;
                }
                op_prec = op;
            }
            var pprec_0 = this.wall.shape[0];
            var pprec_1 = this.wall.shape[1];
            if (op_prec) {
                pprec_0 = cn_add(this.wall.bounds.pmin, cn_mul(this.wall.bounds.direction, op_prec.position + op_prec.get_width()));
                pprec_1 = cn_add(pprec_0, nn);
            }
            html += camera.draw_measure(pprec_0, p0_0, null, false, 2, add_classes);
            html += camera.draw_measure(p1_0, pprec_1, null, false, 2, add_classes);

            if (op_next == null) {
                html += camera.draw_measure(p0_1, this.wall.shape[3], null, false, 2, add_classes);
                html += camera.draw_measure(this.wall.shape[2], p1_1, null, false, 2, add_classes);
            }*/
        }

        return html;
    }

    next_valid_opening() {
        const oi = this.wall.openings.indexOf(this);
        if (oi < 0) return null;
        for (var i = oi + 1; i < this.wall.openings.length; i++) {
            if (this.wall.openings[i].valid) return this.wall.openings[i];
        }
        return null;
    }

    previous_valid_opening() {
        const oi = this.wall.openings.indexOf(this);
        if (oi < 0) return null;
        for (var i = oi - 1; i >= 0; i--) {
            if (this.wall.openings[i].valid) return this.wall.openings[i];
        }
        return null;
    }
    //***********************************************************************************
    //**** Remove an opening
    //***********************************************************************************

    remove() {
        if (this.wall == null) return;
        var index = this.wall.openings.indexOf(this);
        if (index >= 0) this.wall.openings.splice(index, 1);
    }

    //***********************************************************************************
    //**** checks if wall position is ok
    //***********************************************************************************

    check_wall() {
        if (this.wall == null) return false;

        var x0 = this.position;
        var x1 = x0 + this.get_width();
        if (x0 < this.wall.opening_area[0] - 0.001) return false;
        if (x1 > this.wall.opening_area[1] + 0.001) return false;

        for (var i in this.wall.openings) {
            var opening = this.wall.openings[i];
            if (opening == this) continue;
            if (!opening.valid) continue;
            if (x1 <= opening.position + 0.0001) return true;
            if (x0 >= opening.position + opening.get_width() - 0.0001) continue;
            return false;
        }
        return true;
    }

    /**
     * Returns true if other openings overlaps this (regardless of the 'valid' parameters)
     * @param {cn_opening | import('./cn_niche').cn_niche} other_element
     */
    overlaps(other_element) {
        return this.get_vertical_box(-0.001).intersects(other_element.get_vertical_box(-0.001));
    }

    /**
     * returns vertical bounding box on wall
     * @returns {cn_box}
     */
    get_vertical_box(tolerance = 0) {
        let box = new cn_box();
        let z0 = this.compute_offset() + this.get_z();
        box.enlarge_point([this.position, z0]);
        box.enlarge_point([this.position + this.get_width(), z0 + this.get_height()]);
        if (tolerance != 0) box.enlarge_distance(tolerance);
        return box;
    }

    /**
     * Sets best place for an opening, depending on a 3D point. Allows snap for precision.
     * Returns false if no proper position.
     * @param {number[]} position
     * @param {number} precision
     * @returns {boolean}
     */
    optimize_placement(position, precision) {
        let width = this.get_width();

        //*** initial position of the opening */
        var x = cn_dot(cn_sub(position, this.wall.vertices[0].position), this.wall.bounds.direction) - 0.5 * this.get_width();
        if (x < 0) x = 0;
        else if (x + width > this.wall.bounds.length) x = this.wall.bounds.length;

        //*** Build list of allowed position ranges, depending on wall limits and other openings */
        let z0 = this.compute_offset() + this.get_z();
        let z1 = z0 + this.get_height();
        var positions = [];
        positions.push(this.wall.opening_area[0]);
        for (var i in this.wall.openings) {
            var opening = this.wall.openings[i];
            if (opening.valid && opening != this) {
                let zz0 = opening.compute_offset() + opening.get_z();
                let zz1 = zz0 + opening.get_height();
                if (z0 >= zz1 || zz0 >= z1) continue;
                positions.push(opening.position);
                positions.push(opening.position + opening.get_width());
            }
        }
        positions.push(this.wall.opening_area[1]);

        //*** find proper place */
        for (var n = 0; n < positions.length; n += 2) {
            //*** wrong if opening off limits */
            if (x < positions[n] - precision) return false;

            //*** iterate if opening if further than this */
            if (x + width > positions[n + 1] + precision) continue;

            //*** not enough space for the opening : false */
            if (positions[n + 1] - positions[n] < width + 0.0001) return false;

            //*** try snapping */
            var x0 = Math.abs(x - positions[n]);
            var x1 = Math.abs(x + width - positions[n + 1]);
            if (x0 < precision && x0 < x1)
                x = positions[n];
            else if (x1 < precision)
                x = positions[n + 1] - width;

            //*** Apply position */
            this.position = x;
            this.update_wall_position();
            return true;
        }
        return false;
    }

    /**
     * Puts the opening on a wall
     * @param {cn_wall} wall
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    set_wall(wall, transaction_manager) {
        if (wall == this.wall) return;
        if (transaction_manager) {
            transaction_manager.push_item_set(this, 'wall');
            transaction_manager.push_item_set(this.wall, 'openings');
            transaction_manager.push_item_set(wall, 'openings');
        }
        var index = this.wall.openings.indexOf(this);
        if (index >= 0) this.wall.openings.splice(index, 1);
        this.wall = wall;
        wall.openings.push(this);
        this.parent = wall;
    }
    /**
     * Returns actual position of the opening
     * @returns {Array<number>}
     */
    get_position() {
        return cn_add(this.wall.vertex_position(0), cn_mul(this.wall.bounds.direction, this.position));
    }

    /**
     * Returns the opening vertical offset, relatively to 0.
     * @returns {number}
     */
    compute_offset() {
        if (!this.wall) return 0;
        if (!this.wall_interface || !this.wall_interface.spaces[0] || !this.wall_interface.spaces[1]) return 0;
        if (this.wall.dormer) return this.wall.dormer.get_opening_height(this);
        const facade_0 = this.wall_interface.spaces[0].outside;
        const facade_1 = this.wall_interface.spaces[1].outside;

        if (!facade_0 && !facade_1) {
            return Math.max(this.wall_interface.spaces[0].slab_offset, this.wall_interface.spaces[1].slab_offset);
        } else if (!facade_0) {
            return this.wall_interface.spaces[0].slab_offset;
        } else if (!facade_1) {
            return this.wall_interface.spaces[1].slab_offset;
        }
        return 0;
    }

    //******************************************************
    //**** Build 3D footprint
    //******************************************************
    build_footprint(z) {
        var polygon = new fh_polygon([0, 0, z], [0, 0, 1]);

        var v0 = cn_mul(this.wall.bounds.direction, this.position);
        var v1 = cn_mul(this.wall.bounds.direction, this.position + this.get_width());
        var p0 = cn_add(this.wall.bounds.pmin, v0);
        var p1 = cn_add(this.wall.bounds.pmin, v1);
        var p2 = cn_add(this.wall.bounds.pmax, v1);
        var p3 = cn_add(this.wall.bounds.pmax, v0);
        p0.push(z);
        p1.push(z);
        p2.push(z);
        p3.push(z);
        polygon.add_contour([p0, p1, p2, p3]);

        return polygon;
    }

    //******************************************************
    //*** Build 3D geometry
    //******************************************************
    build_extruded_polygons(z) {
        if (this.wall == null || !this.valid) return [];
        var extruded_polygons = this.get_opening_type().build_extruded_polygons();

        var matrix = this.build_3d_matrix(z);
        for (var k = 0; k < extruded_polygons.length; k++)
            extruded_polygons[k].matrix = matrix;
        return extruded_polygons;
    }

    //******************************************************
    //*** Build 3D geometry
    //******************************************************
    build_3d_matrix(z) {
        let thinnest_wall = this.wall.get_thinnest_dummy_wall();
        var p = cn_add(thinnest_wall.bounds.pmin, cn_mul(thinnest_wall.bounds.direction, this.position));
        var delta = 0;
        if (this.opening_position == 0)
            delta = this.thickness;
        else if (this.opening_position == 1)
            delta = 0.5 * thinnest_wall.wall_type.thickness + 0.5 * this.thickness;
        else
            delta = thinnest_wall.wall_type.thickness;

        if (!this.opening_start) {
            delta -= this.thickness;
            p = cn_add(p, cn_mul(thinnest_wall.bounds.direction, this.get_width()));
        }

        p = cn_add(p, cn_mul(thinnest_wall.bounds.normal, delta));

        var dx = [thinnest_wall.bounds.direction[0], thinnest_wall.bounds.direction[1], 0];
        if (!this.opening_start) dx = fh_mul(dx, -1);

        var dy = [0, 0, 1];
        var dz = fh_cross(dx, dy);
        var origin = [p[0], p[1], z + this.get_z()];

        var matrix = new fh_matrix();
        matrix.load_axis(origin, dx, dy, dz);
        return matrix;
    }

    //******************************************************
    //*** Build 3D geometry
    //******************************************************
    build_closing_3d_matrix(z) {
        let dx, origin;
        if (this.wall_interface.get_flow_direction()) {
            origin = cnx_add(this.wall.bounds.pmax, cn_mul(this.wall.bounds.direction, this.position + this.get_width()));
            dx = cnx_mul(this.wall.bounds.direction, -1);
        }
        else {
            origin = cnx_add(this.wall.bounds.pmin, cn_mul(this.wall.bounds.direction, this.position));
            dx = cnx_mul(this.wall.bounds.direction, 1);
        }
        var dy = [0, 0, 1];
        var dz = fh_cross(dx, dy);
        origin[2] = z;
        origin = cnx_add(origin, cnx_mul(dz, 0.01));
        var matrix = new fh_matrix();
        matrix.load_axis(origin, dx, dy, dz);
        return matrix;
    }

    //***********************************************************************************
    //**** Contained by box ?
    //***********************************************************************************
    contained_by_box(box) {
        if (this.wall == null) return false;
        var v0 = cn_mul(this.wall.bounds.direction, this.position);
        var v1 = cn_mul(this.wall.bounds.direction, this.position + this.get_width());
        var p = cn_add(this.wall.bounds.pmin, v0);
        if (!box.contains_point(p)) return false;
        p = cn_add(this.wall.bounds.pmin, v1);
        if (!box.contains_point(p)) return false;
        p = cn_add(this.wall.bounds.pmax, v1);
        if (!box.contains_point(p)) return false;
        p = cn_add(this.wall.bounds.pmax, v0);
        if (!box.contains_point(p)) return false;
        return true;
    }

    contains(p, tolerance = 0) {
        if (!this.wall) return false;
        let y = cn_dot(this.wall.bounds.normal, (cn_sub(p, this.wall.bounds.pmin)));
        if (y < -tolerance || y > this.wall.wall_type.thickness + tolerance) return false;
        let a = this.wall.compute_abcissa(p);
        if (a < this.position - tolerance) return false;
        if (a > this.position + this.get_width() + tolerance) return false;
        return true;
    }

    //***********************************************************************************
    //***Reverse the wall */
    reverse() {
        this.opening_position = 2 - this.opening_position;
        //this.opening_start = !this.opening_start;
        this.opening_inside = !this.opening_inside;
    }

    /**
     * Accept element visitor
     *
     * @param {cn_element_visitor} element_visitor
     */
    accept_visitor(element_visitor) {
        element_visitor.visit_opening(this);
    }

    get_bounding_box() {
        const box = new cn_box();
        const p0 = this.wall.bounds.pmin;
        const dir = this.wall.bounds.direction;
        box.enlarge_point(cn_add(p0, cn_mul(dir, this.position)));
        box.enlarge_point(cn_add(p0, cn_mul(dir, this.position + this.get_width())));
        return box;
    }

    /**
     * Returns the depth of the opening, from exterior side.
     * Cannot return a negative value.
     * @returns {number}
     */
    get_outside_depth() {
        if (this.opening_type.free) return this.wall.wall_type.thickness;
        if (this.opening_position == 1) return Math.max(0, 0.5 * (this.wall.wall_type.thickness - this.thickness));
        if (this.wall_interface && (this.opening_position == 0) != this.wall_interface.get_flow_direction()) return 0;
        return Math.max(0, this.wall.wall_type.thickness - this.thickness);
    }

    get_outer_side() {
        return (this.wall && this.wall.get_flow_direction()) ? 1 : 0;
    }

    /**
     * Returns true if the input side is outside.
     * @param {number} side
     * @returns {boolean}
     */
    is_outdoor_side(side) {
        if (!this.wall_interface) return false;
        return this.wall_interface.spaces[side].outside;
    }

    /**
     * Returns the number of different facing parts.
     * @returns {number[]}
     */
    get_facings_codes(side) {
        if (this.opening_type.free) return [];
        let nf = [CN_OPENING_FACING_SASH];
        if (this.opening_type.opening != "none") nf.push(CN_OPENING_FACING_OPENING);
        if (this.opening_type.transom != "none") nf.push(CN_OPENING_FACING_TRANSOM);
        if (this.opening_type.sill != "none") nf.push(CN_OPENING_FACING_SILL);
        if (this.opening_type.closing != "none" && side == this.get_outer_side()) nf.push(CN_OPENING_FACING_CLOSING);
        return nf;
    }

    get_facing_groups() {
        let low_sil_label = (this.get_z() > 0) ? "Appui" : "Seuil";
        let facing_groups = [];
        if (this.opening_type.free) {
            facing_groups.push(new cn_facing_group("Revêtements d'encadrement", "hole", 0, CN_FACING_SUPPORTS.WALL, [
                new cn_constant('Tableau', 'lowsill'),
                new cn_constant('Embrasure', 'reveal'),
                new cn_constant('Linteau', 'lintel')
            ]));
            if (this.opening_type.frame != "none") {
                let facing_group = new cn_facing_group(`Revêtement de dormant`, "element", 0, CN_FACING_SUPPORTS.OPENING, [
                    new cn_constant('Dormant', 'sash')]);
                facing_groups.push(facing_group);
            }
            return facing_groups;
        }
        for (var side = 0; side < 2; side++) {
            let outdoor_side = this.is_outdoor_side(side);
            let position = (outdoor_side) ? "extérieur" : "intérieur";
            let facing_group = new cn_facing_group(`Revêtements ${position} de baie`, "element", side, CN_FACING_SUPPORTS.OPENING, [
                new cn_constant('Dormant', 'sash')]);
            facing_groups.push(facing_group);
            if (this.opening_type.opening != "none")
                facing_group.roles.push(new cn_constant('Ouvrant', 'opening'));
            if (this.opening_type.transom != "none")
                facing_group.roles.push(new cn_constant('Imposte', 'transom'));
            if (this.opening_type.sill != "none")
                facing_group.roles.push(new cn_constant('Allège', 'sill'));
            if (this.opening_type.closing != "none" && side == this.get_outer_side())
                facing_group.roles.push(new cn_constant('Fermeture', 'closing'));
            if (side == this.get_outer_side())
                facing_group.roles.push(new cn_constant('Garde-corps', 'railing'));

            if (side * 2 != this.opening_position) {
                facing_groups.push(new cn_facing_group(`Revêtements ${position} d'encadrement`, "hole", side, (outdoor_side) ? CN_FACING_SUPPORTS.FACADE : CN_FACING_SUPPORTS.WALL, [
                    new cn_constant(low_sil_label, 'lowsill'),
                    new cn_constant('Embrasure', 'reveal'),
                    new cn_constant('Linteau', 'lintel')
                ]));
            }
        }

        return facing_groups;

    }

    static get_all_facing_groups() {
        let facing_groups = [];
        facing_groups.push(new cn_facing_group(`Revêtements de baie`, "element", 0, CN_FACING_SUPPORTS.OPENING, [
            new cn_constant('Dormant', 'sash'),
            new cn_constant('Ouvrant', 'opening'),
            new cn_constant('Imposte', 'transom'),
            new cn_constant('Allège', 'sill')]));

        facing_groups.push(new cn_facing_group(`Revêtements d'encadrement de baie`, "hole", 0, CN_FACING_SUPPORTS.WALL, [
            new cn_constant('Appui', 'lowsill'),
            new cn_constant('Embrasure', 'reveal'),
            new cn_constant('Linteau', 'lintel')]));
        return facing_groups;
    }

    compute_facing_count() {
        [0, 1].forEach(side => {
            for (var key in this.facings[side]) {
                if (this.facings[side][key])
                    this.facings[side][key].count++;
            }
        });
    }

    
    /**
     * TODO : derivate is element can be aligned
     * @returns {boolean}
     */
    is_alignable() {
        return this.valid;
    }

    /**
     * TODO : derivate this to manage alignment
     * Returns current align center
     * @returns {number[]}
     */
    get_align_center() {
        let x = this.position + 0.5 * this.get_width();
        let p0 =this.wall.compute_side_position_from_abcissa(x,0);
        let p1 =this.wall.compute_side_position_from_abcissa(x,1);
        return cn_middle(p0,p1);
    }

    /**
     * TODO : derivate this to manage alignment
     * Returns true if center was successfully set
     * @param {number[]} p
     * @param {number[]} center
     * @param {import('../utils/cn_transaction_manager').cn_transaction_manager} transaction_manager
     * @returns {boolean}
     */
    set_align_center(p, center, transaction_manager = null) {
        let w = this.wall.parent.find_wall(p,0.01);
        if (w) {
            this.set_wall(w,transaction_manager);
            this.position = this.wall.compute_abcissa(p) - 0.5 * this.get_width();
        }
        else {
            let dir = cn_sub(p,center);
            if (cn_normalize(dir) < 0.001) return false;
            let impact0 = this.wall.parent.raytrace_walls(p,dir,1000);
            let impact1 = this.wall.parent.raytrace_walls(p,cn_mul(dir,-1),1000);
            let impact = (impact0 && impact1)?impact0.distance < impact1.distance?impact0:impact1:impact0 || impact1;
            if (!impact) return false;
            
            this.set_wall(impact.wall,transaction_manager);
            this.position = impact.abcissa - 0.5 * this.get_width();
        }

        this.update_wall_position();
        if (transaction_manager)
            this.wall.parent.update_space_numerotation();
        return true;
    }
}
